import { ArrowRight } from "lucide-react";
import FadeIn from "./FadeIn";
import { C } from "./brandTokens";

interface FooterCTASectionProps {
  onCTA: () => void;
}

export default function FooterCTASection({ onCTA }: FooterCTASectionProps) {
  return (
    <section id="about-eden" className="relative overflow-hidden py-[100px] px-6 sm:px-10" style={{ background: C.brown }}>
      {/* Cross motifs */}
      {[{ top: "15%", left: "5%", size: 160 }, { bottom: "10%", right: "6%", size: 120 }].map((pos, i) => (
        <div key={i} className="absolute opacity-[0.05] hidden md:block" style={{ ...pos, width: pos.size, height: pos.size }}>
          <div className="absolute top-1/2 left-0 right-0 h-px -translate-y-1/2" style={{ background: C.cream }} />
          <div className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2" style={{ background: C.cream }} />
        </div>
      ))}

      <div className="max-w-[560px] mx-auto text-center relative">
        <FadeIn>
          {/* ── Founder quote ── */}
          <div className="mb-14">
            <span
              className="font-heading inline-block leading-none select-none mb-4"
              style={{ fontSize: 56, color: "rgba(250,248,243,0.12)" }}
            >
              &ldquo;
            </span>
            <div className="space-y-4 font-heading text-[17px] sm:text-[19px] leading-[1.85] italic" style={{ color: "rgba(250,248,243,0.65)" }}>
              <p>
                I built Eden Pages because I kept failing at the devotional life I wanted. Every tool I tried was built for productivity — not for the soul.
              </p>
              <p>
                Eden Pages is what I wished existed: one quiet place where writing, scripture, and prayer come together — and where the practice actually sticks.
              </p>
            </div>
            <p
              className="font-heading italic"
              style={{ fontSize: 13, color: "rgba(250,248,243,0.55)", letterSpacing: "0.3px", marginTop: 24, marginBottom: 6 }}
            >
              — Jeremy Ward, Founder
            </p>
            <p
              className="font-ui"
              style={{ fontSize: 12, color: "rgba(250,248,243,0.38)", letterSpacing: "0.3px", lineHeight: 1.6, marginBottom: 0 }}
            >
              Writer and product designer. Building Eden Pages for the Christians who keep trying — and keep coming back to the blank page.
            </p>
            <p
              className="font-ui"
              style={{ fontSize: 11, color: "rgba(250,248,243,0.30)", letterSpacing: "1px", marginTop: 8 }}
            >
              BUILT BY PEOPLE OF FAITH, FOR PEOPLE OF FAITH
            </p>
          </div>

          {/* ── CTA ── */}
          <div className="pt-10" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <h2 className="font-heading font-semibold leading-[1.1] mb-5" style={{ fontSize: "clamp(30px, 3.5vw, 46px)", color: C.cream }}>
              Your practice is<br />waiting for you.
            </h2>

            <p className="font-ui text-[15px] leading-[1.75] mb-8 max-w-[400px] mx-auto" style={{ color: "rgba(250,248,243,0.55)" }}>
              Ten mornings free. No credit card. No auto-charge. Just open the page and write.
            </p>

            <button
              onClick={onCTA}
              className="font-ui text-sm font-medium border-none rounded-lg cursor-pointer inline-flex items-center gap-2 transition-transform duration-200 hover:-translate-y-0.5"
              style={{
                color: C.brown,
                background: C.cream,
                padding: "16px 36px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              }}
              onMouseEnter={() => import("@/components/AuthModal")}
            >
              Start Your First Entry <ArrowRight size={15} />
            </button>

            {/* Closing verse */}
            <div className="mt-10 pt-8" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <p className="font-heading italic leading-[1.7] mb-2" style={{ fontSize: "clamp(14px, 1.6vw, 17px)", color: "rgba(250,248,243,0.4)" }}>
                "You will seek me and find me when you seek me with all your heart."
              </p>
              <span className="font-ui text-[11px] tracking-[1.4px]" style={{ color: "rgba(250,248,243,0.25)" }}>
                JEREMIAH 29:13
              </span>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
